import { useState, useEffect } from "react";
import { FONT, COLOR } from "../lib/tokens";

// GetStartedCard — the new-user checklist on Home. Rows track REAL
// signals (pantry rows, own recipes, first cook, connections), each
// taps through to the surface that completes it, and the whole card
// retires itself when everything's done (or on manual dismiss). The
// item SET follows the cooking-style persona: a keep-it-simple cook
// is never told to inventory their kitchen.
//
// Gating (checked here so Home stays clean): onboarded_at present,
// not dismissed for this user, not all-done. All-done has two paths:
//   - already complete at MOUNT (every existing user after the 0281
//     backfill) → retire silently, persist the dismissal, render
//     nothing — no stale "you're fully cooking" card on update day.
//   - completes DURING the session → show the celebration once, then
//     auto-dismiss after a beat.

const DISMISS_KEY = (userId) => `booda:getStartedDismissed:${userId}`;

const ITEM_SETS = {
  simple:   ["generate", "cook"],
  dayof:    ["generate", "cook", "friend"],
  explorer: ["generate3", "cook", "friend"],
  stocker:  ["stock", "generate", "cook", "friend"],
};

export default function GetStartedCard({
  userId,
  profile,
  pantryCount = 0,
  ownRecipeCount = 0,
  ownCuisineCount = 0,
  connectionsCount = 0,
  onOpenKitchen,
  onOpenCreate,
  onOpenFindFriends,
}) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY(userId)) === "1"; }
    catch { return false; }
  });
  // Flips true the first time the card renders with work remaining —
  // distinguishes "finished while using the app" (celebrate) from
  // "was already finished when Home mounted" (retire silently).
  const [sawIncomplete, setSawIncomplete] = useState(false);

  const cookedOnce = !!profile?.last_cooked_date;
  const style = profile?.cook_style || "stocker";
  const ids = ITEM_SETS[style] || ITEM_SETS.stocker;

  const ITEMS = {
    stock:     { label: "Stock your kitchen",            hint: "Add 3 items — scan or type", done: pantryCount >= 3, go: onOpenKitchen },
    generate:  { label: "Generate your first recipe",    hint: "Tap + and ask for anything", done: ownRecipeCount >= 1, go: onOpenCreate },
    generate3: { label: "Generate 3 cuisines",           hint: "Three recipes, three cuisines", done: ownCuisineCount >= 3, go: onOpenCreate },
    cook:      { label: "Cook something",                hint: "Step-by-step with timers", done: cookedOnce, go: onOpenCreate },
    friend:    { label: "Add family or a friend",        hint: "Cook together, share recipes", done: connectionsCount >= 1, go: onOpenFindFriends },
  };
  const rows = ids.map((id) => ({ id, ...ITEMS[id] })).filter((r) => r.go);
  const doneCount = rows.filter((r) => r.done).length;
  const allDone = doneCount === rows.length && rows.length > 0;
  const gatedOff = !profile?.onboarded_at || dismissed;
  const retiredSilently = allDone && !sawIncomplete;

  useEffect(() => {
    if (gatedOff) return;
    if (!allDone) {
      setSawIncomplete(true);
      return;
    }
    if (retiredSilently) {
      // Already done at mount — persist so we never re-evaluate.
      try { localStorage.setItem(DISMISS_KEY(userId), "1"); } catch { /* noop */ }
      return;
    }
    // Completed mid-session: let the celebration sit, then retire.
    const t = setTimeout(() => {
      setDismissed(true);
      try { localStorage.setItem(DISMISS_KEY(userId), "1"); } catch { /* noop */ }
    }, 8000);
    return () => clearTimeout(t);
  }, [gatedOff, allDone, retiredSilently, userId]);

  if (gatedOff || retiredSilently) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY(userId), "1"); } catch { /* noop */ }
  };

  return (
    <div style={{
      margin: "16px 16px 4px", padding: "16px 16px 8px",
      background: COLOR.ground, border: `1px solid ${COLOR.border}`,
      borderRadius: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.14em", color: COLOR.dim,
          }}>
            GET STARTED
          </div>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 20, color: COLOR.ink, marginTop: 2,
          }}>
            {allDone ? "You're fully cooking." : `${doneCount} of ${rows.length} done`}
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss checklist"
          style={{
            width: 32, height: 32, borderRadius: 16, flexShrink: 0,
            background: "transparent", border: "none",
            fontFamily: FONT.forma, fontSize: 16, color: COLOR.sub,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>

      {allDone ? (
        <div style={{
          padding: "10px 0 14px",
          fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.5,
        }}>
          Kitchen stocked, recipes flowing, dinner cooked. Tap × and go
          make something great.
        </div>
      ) : (
        rows.map((r) => (
          <button
            key={r.id}
            onClick={r.done ? undefined : r.go}
            disabled={r.done}
            className={r.done ? undefined : "mcm-focusable"}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "12px 2px", background: "transparent", border: "none",
              borderTop: `1px solid ${COLOR.edge}`,
              cursor: r.done ? "default" : "pointer", textAlign: "left",
            }}
          >
            <span style={{
              width: 24, height: 24, borderRadius: 12, flexShrink: 0,
              border: `2px solid ${r.done ? COLOR.leaf : COLOR.border}`,
              background: r.done ? COLOR.leaf : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 13, fontWeight: 700, lineHeight: 1,
            }}>
              {r.done ? "✓" : ""}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                display: "block",
                fontFamily: FONT.forma, fontSize: 14,
                fontWeight: 600,
                color: r.done ? COLOR.sub : COLOR.ink,
                textDecoration: r.done ? "line-through" : "none",
              }}>
                {r.label}
              </span>
              {!r.done && (
                <span style={{
                  display: "block", marginTop: 1,
                  fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted,
                }}>
                  {r.hint}
                </span>
              )}
            </span>
            {!r.done && (
              <span aria-hidden style={{ fontFamily: FONT.forma, fontSize: 14, color: COLOR.sub, flexShrink: 0 }}>
                ›
              </span>
            )}
          </button>
        ))
      )}
    </div>
  );
}
